<template>
    <VJoyDialog
        v-if="isOnboardingSectionVisible"
        ref="onboardingStrategyDialog"
        data-testid="malt-strategy-onboarding-dialog"
        class="onboarding-strategy"
        @dialog:hide="acknowledgeOnboardingDialog"
    >
        <template #dialog-subheader>
            <div class="onboarding-strategy__subheader">
                <span class="welcome-tag">{{ t('profile.edition.onboarding.strategy.dialog.welcome.tag') }}</span>
                <MaltImg class="logo" src="/malt-logos/malt_strategy_logo" alt="" width="235" />
                <div class="shapes">
                    <Shapes />
                </div>
            </div>
        </template>

        <template #dialog-body>
            <div class="onboarding-strategy__content">
                <h2 class="title">{{ t('profile.edition.onboarding.strategy.dialog.title') }}</h2>
                <div class="subtitle">{{ t('profile.edition.onboarding.strategy.dialog.paragraph.1') }}</div>
                <div class="text-content">{{ t('profile.edition.onboarding.strategy.dialog.paragraph.2') }}</div>
            </div>
        </template>
    </VJoyDialog>
</template>
<script setup lang="ts">
    import {computed, onMounted, useTemplateRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import {MaltImg} from '#components';
    import {useTranslation} from '#imports';
    import {VJoyDialog} from '@maltjoy/core-vue';
    import {useSectionsVisibilityStore} from '~/store/sectionsVisibility';
    import {useWebPreferencesStore} from '~/store/webPreferences.store';
    import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';
    import Shapes from '@/components/_common/shapes/Shapes.vue';

    const {t} = useTranslation();
    const onboardingStrategyDialog = useTemplateRef('onboardingStrategyDialog');
    const webPreferencesStore = useWebPreferencesStore();

    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());
    const isOnboardingSectionVisible = computed(() => sectionVisibility.value[SectionsVisibilityType.ONBOARDING_STRATEGY]);

    onMounted(() => {
        if (isOnboardingSectionVisible.value && onboardingStrategyDialog.value) {
            onboardingStrategyDialog.value.show();
        }
    });

    async function acknowledgeOnboardingDialog(): Promise<void> {
        return await webPreferencesStore.acknowledgeOnboarding();
    }
</script>
<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .joy-dialog--close {
        border-radius: var(--joy-core-radius-6);
    }

    :global(.onboarding-strategy .joy-dialog--subheader) {
        position: static;
        margin-top: 238px;
    }

    .onboarding-strategy {
        &__subheader {
            position: absolute;
            top: 0;
            left: 0;
            height: 270px;
            width: 100%;
            background-color: var(--joy-color-secondary-10);

            .welcome-tag {
                position: absolute;
                top: 54px;
                left: var(--joy-core-spacing-8);
                @include text.getFontBySize(primary-450);
                color: var(--joy-color-neutral-60);
            }

            .logo {
                position: absolute;
                top: 85px;
                left: var(--joy-core-spacing-8);
                width: 235px;
            }

            .shapes {
                position: absolute;
                top: 35px;
                right: 0px;
            }
        }

        &__content {
            margin-top: var(--joy-core-spacing-3);
            margin-bottom: var(--joy-core-spacing-7);

            .title {
                @include text.getFontBySize(secondary-200);
                margin-bottom: var(--joy-core-spacing-4);
                color: var(--joy-color-neutral-60);
            }
            .subtitle,
            .text-content {
                @include text.getFontBySize(primary-400);
            }
            .subtitle {
                margin-bottom: var(--joy-core-spacing-4);
            }
            .text-content {
                font-weight: var(--joy-font-weight-bold);
            }
        }
    }
</style>
